import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'react-scrollbars-custom';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import CacheData from '../../../common/utils/cacheData';
import PrintPdfDropdown from '../../../common/export_documents/PrintPdfDropdown';
import { showLoader, hideLoader } from '../../../store/common/slice';
import Search from '../../../images/search.svg';
import { StyledPdfWrapper } from '../debtList/PdfWrapper.styled';
import YearlyTenantPdfRow from '../yearlyByMonths/YearlyTenantPdfRow';
import { BorderedPageWrapperStyled } from './TenantList.styled';
import TenantCard from './TenantCard';
import ApartmentInfo from './ApartmentInfo';
import { PageTitle } from '../../../common/PageTitle';
import {constants} from "../../../common/constants/constants";
import BllinkUtils from "../../../common/utils/utils";

const TenantListNew = ({ match }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const titleData = useSelector(state => state.buildings.titleData);
    const ltr = useSelector(state => state.common.ltrEnabled);
    const client = useAccessToken();
    const { buildingID } = match.params;
    const currentApt = CacheData.fetchCache('currentAptInTenantList');

    const pdfContainer = useRef();

    const [filter, setFilter] = useState('');
    const [apartments, setApartments] = useState([]);
    const [selectedApt, setSelectedApt] = useState();
    const [edit, setEdit] = useState(false);
    const [add, setAdd] = useState(false);
    const [parsedRecords, setParsedRecords] = useState([]);
    const [pdfContainerActive, setPdfContainerActive] = useState(false);
    const [address, setAddress] = useState("");
    const pdfContainerActivate = () => setPdfContainerActive(true);

    const titleRow = [{
        apartmentNum: t('reports.apartmentName'),
        OwnerName: t('reports.ownerName'),
        OwnerPhone: t('reports.ownerPhone'),
        OwnerEmail: t('reports.ownerEmail'),
        RenterName: t('reports.renterName'),
        RenterPhone: t('reports.renterPhone'),
        RenterEmail: t('reports.renterEmail'),
        monthlyFee: t('reports.monthlyFee')
    }];

    const loadApartments = async () => {
        dispatch(showLoader());
        const response = await client.tenants.getTenantsForBuilding(buildingID);
        setApartments(response.apartments.sort((a, b) => a.apartmentNum - b.apartmentNum));
        setSelectedApt(response.apartments[currentApt] || response.apartments[0]);
        dispatch(hideLoader());
    };

    useEffect(async () => {
        if (!client) return;

        await loadApartments();
    }, [client]);

    useEffect(() => {
        setSelectedApt(apartments[currentApt] || apartments[0])
    }, [apartments]);

    useEffect(() => {
        titleData && setAddress(`${titleData.street},${titleData.city}`);
    }, [titleData]);

    useBodyClass('report');

    const renderTenantsCards = (apartment, i) => {
        const onClick = num => {
            CacheData.cache('currentAptInTenantList', num);
            CacheData.delete('currentContactInTenantList');
            setSelectedApt(apartments[num]);
        };

        return (
            <TenantCard 
                key={i} 
                apt={apartment} 
                selectedApt={selectedApt} 
                num={i}
                onClick={onClick}
                filter={filter}
                csvCallback={csvCallback}
            />
        )
    };

    const firstContainerRef = useRef();

    useEffect(() => {
        const onClick = e => {
            if (firstContainerRef?.current?.contains(e.target)) {
                setEdit(false);
                setAdd(false);
            }
            else return 
        }
        document.addEventListener('click', onClick);
    }, []);

    const csvCallback = item => setParsedRecords(prev => prev.concat([item]));

    const getParsedRecords = (type) => {
        if (type === 'pdf') {
            const records = parsedRecords.map(r => {
                r.monthlyFee = BllinkUtils.Strings.numberWithCommas(r.monthlyFee);
                return r;
            });
            setParsedRecords(records);
        }
        return parsedRecords;
    }

    const renderTenantRows = (apt, i) => <YearlyTenantPdfRow apt={apt} key={i} />;

    return (
        <BorderedPageWrapperStyled ltr={ltr}>
            <PageTitle text={t('buildings.nav_bar_tenant_list')} subtitle={t('reports.tenant_list_subtitle')}>
                <PrintPdfDropdown
                    buildingID={buildingID}
                    eventLabel="tenantsList"
                    titleString={t('reports.tenant_list_title')}
                    excelTitle={t('reports.tenant_list_title')}
                    reportName={t('buildings.nav_bar_tenant_list')}
                    pdfContainer={pdfContainer.current}
                    onClick={pdfContainerActivate}
                    rows={getParsedRecords}
                    pdfIframe
                    pdfSettings={{ chunkSize: 18, orientation: constants.pages.pdfOrientation.landscape }}
                    titleRow={titleRow}
                    xlsSettings={{ columnTypes: [{index: 7, type: 'Number'}] }}
                    address={address}
                />
            </PageTitle>

            <div className='main-wrapper'>
                <div className='first-container' ref={firstContainerRef}>
                    <div className='top-bar'>
                        <div className='search'>
                            <input
                                type='text'
                                name='search'
                                onChange={e => setFilter(e.target.value.toLowerCase())}
                                value={filter}
                                placeholder={t('reports.fast_search')}
                            />
                            <img src={Search} alt={t('search.search')} />
                        </div>
                        <div className='total-apartments'>
                            {t('reports.total_apartments')}: <span>{apartments?.length}</span>
                        </div>
                    </div>

                    <Scrollbar
                        style={{ width: '100%', height: 'calc(100vh - 100px)' }}
                        trackYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-track" />;
                            }
                        }}
                        thumbYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                            }
                        }}
                        rtl={false}
                        noScrollX
                    >
                        <div className='cards-wrapper'>
                            {apartments.map((apartment, i) => renderTenantsCards(apartment, i))}
                        </div>                  
                    </Scrollbar>
                </div>

                <ApartmentInfo 
                    apt={selectedApt} 
                    buildingID={buildingID} 
                    loadApartments={loadApartments} 
                    edit={edit}
                    setEdit={setEdit}
                    add={add}
                    setAdd={setAdd}
                    setApartments={setApartments}
                    apartments={apartments}
                />

            </div>

            <div id="section-to-print" ref={pdfContainer}>
                {pdfContainerActive && (
                    <StyledPdfWrapper ltr={ltr}>
                        <div className='pdf-title'>{t('reports.tenant_list_title')}</div>

                        <div className='pdf-rows-wrapper'>
                            <div className='pdf-row'>
                                <div className='pdf-highlited yearly-apt'>{t('reports.apartmentName')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.ownerName')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.ownerPhone')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.ownerEmail')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.renterName')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.renterPhone')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.renterEmail')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.monthlyFee')}</div>
                            </div>

                            {apartments.map((apt, i) => renderTenantRows(apt, i))}
                        </div>
                    </StyledPdfWrapper>
                )}
            </div>
        </BorderedPageWrapperStyled>
    )
}

export default TenantListNew;
